import React from "react";
import { HeartHandshake, Compass, BookOpen, Quote, Sparkles, ShieldCheck } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const piliers = [
  {
    icon: Compass,
    titre: "Suivi individualisé",
    texte:
      "Chaque élève bénéficie d'entretiens réguliers avec un référent pour faire le point sur son parcours scolaire et personnel.",
  },
  {
    icon: BookOpen,
    titre: "Soutien scolaire",
    texte:
      "Heures de soutien, aide aux devoirs et tutorat entre pairs pour consolider les acquis.",
  },
  {
    icon: HeartHandshake,
    titre: "Aide à l'orientation",
    texte:
      "Un accompagnement pas à pas pour construire son projet, choisir son stage et préparer la poursuite d'études.",
  },
  {
    icon: Sparkles,
    titre: "Projets éducatifs",
    texte:
      "Sorties culturelles, projets solidaires et interventions de professionnels rythment l'année.",
  },
];

const temoignages = [
  {
    nom: "Camille, élève en Bac Pro MCV",
    citation:
      "Au début j'étais timide, mais ici les profs sont à l'écoute. J'ai pris confiance en moi et j'ai trouvé un stage qui me plaît vraiment.",
  },
  {
    nom: "Sandrine, maman d'élève",
    citation:
      "Mon fils a retrouvé l'envie d'apprendre. Le suivi est régulier, on se sent associés à son parcours. C'est rassurant.",
  },
  {
    nom: "Yanis, ancien élève",
    citation:
      "J'ai eu mon CAP puis mon Bac Pro ici. Aujourd'hui je travaille dans une enseigne d'Angers. Merci à toute l'équipe.",
  },
];

export default function Accompagnement() {
  return (
    <div data-testid="accompagnement-page">
      <PageHero
        eyebrow="Accompagnement & vie scolaire"
        title="Un cadre rassurant pour grandir et réussir"
        subtitle="Notre projet éducatif place l'élève au centre. Bienveillance, exigence et écoute structurent chaque journée passée au lycée."
        image={IMAGES.abstract}
      />

      <Section eyebrow="Nos piliers" title="Quatre engagements concrets">
        <div className="grid gap-6 sm:grid-cols-2">
          {piliers.map((p, i) => (
            <Card key={p.titre} data-testid={`pilier-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-sand/30 text-brand-slate-dark">
                <p.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-bold text-brand-slate-dark">
                {p.titre}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.texte}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section wrapper="bg-brand-sand-wrapper/40" eyebrow="Vie scolaire" title="Une école où l'on se sent bien">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-start">
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              Notre règlement intérieur, construit avec les élèves, repose sur le
              respect mutuel et la responsabilité. Les délégués participent
              activement à la vie de l'établissement à travers le conseil de la
              vie lycéenne.
            </p>
            <p>
              Chaque journée commence par un temps d'accueil et se ponctue
              d'espaces d'échange : repas partagé, récréations encadrées, ateliers
              du midi (sport, théâtre, jeux). L'objectif : créer du lien et
              prévenir l'isolement.
            </p>
            <ul className="mt-6 grid gap-3 text-sm">
              {[
                "Référent de classe & entretiens individuels",
                "Permanence d'écoute psychologique",
                "Médiation entre élèves & accompagnement éducatif",
                "Lien régulier avec les familles",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-brand-slate-dark">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-sage-dark" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.classroom} alt="Vie scolaire" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section eyebrow="Témoignages" title="Ils en parlent mieux que nous">
        <div className="grid gap-6 md:grid-cols-3">
          {temoignages.map((t, i) => (
            <Card key={t.nom} data-testid={`temoignage-${i}`} className="bg-brand-sand-wrapper/40">
              <Quote className="h-7 w-7 text-brand-sand-dark" />
              <p className="mt-5 text-base leading-relaxed text-brand-slate-dark">« {t.citation} »</p>
              <p className="mt-6 text-sm font-semibold text-muted-foreground">— {t.nom}</p>
            </Card>
          ))}
        </div>
      </Section>
    </div>
  );
}
